'use client';

import { useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowsOut, X, Download, Spinner, Image as ImageIcon, ArrowClockwise, ArrowsInSimple, ArrowsOutSimple, CaretDown, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VisualizationProvider, ViewMode } from './context';

type ExportFormat = 'png' | 'jpeg' | 'webp' | 'svg';

const exportFormats: { id: ExportFormat; label: string; extension: string }[] = [
  { id: 'png', label: 'PNG', extension: 'png' },
  { id: 'jpeg', label: 'JPEG', extension: 'jpg' },
  { id: 'webp', label: 'WebP', extension: 'webp' },
  { id: 'svg', label: 'SVG', extension: 'svg' },
];

interface VisualizationWrapperProps {
  title?: string;
  children: ReactNode;
  /** Minimum height in normal mode */
  minHeight?: number;
  /** If true, container height grows with content instead of being fixed */
  flexHeight?: boolean;
  /** Whether to allow fullscreen mode */
  allowFullscreen?: boolean;
  /** Whether to show fit/scroll toggle (for scrollable visualizations) */
  allowFitToggle?: boolean;
  /** Initial view mode — set to 'fit' to default-open in fit-to-screen when the model signals the visual benefits from it */
  initialViewMode?: 'scroll' | 'fit';
  /** Data for export - type and content for renderer */
  exportData?: {
    type: 'flowchart' | 'timeline' | 'comparison-table' | 'concept-map' | 'cycle-diagram' | 'hierarchy' | 'graph' | 'mermaid' | 'html' | 'svg';
    content: unknown;
  };
  /** Extra action buttons (e.g. share) rendered in header */
  renderActions?: ReactNode;
  /** Visualization UUID for URL param tracking */
  visualizationId?: string;
  /** Header chrome placement in normal mode */
  headerMode?: 'overlay' | 'outside';
  className?: string;
}

export function VisualizationWrapper({
  title,
  children,
  minHeight = 400,
  flexHeight = false,
  allowFullscreen = true,
  allowFitToggle = false,
  initialViewMode = 'scroll',
  exportData,
  renderActions,
  visualizationId,
  headerMode = 'overlay',
  className,
}: VisualizationWrapperProps) {
  const pathname = usePathname();
  const isScreenshotMode = pathname.startsWith('/screenshot/');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCheckingCache, setIsCheckingCache] = useState(false);
  const [exportedImage, setExportedImage] = useState<string | null>(null);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('png');
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [containerWidth, setContainerWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);
  const formatDropdownRef = useRef<HTMLDivElement>(null);

  // Auto-open fullscreen if URL has ?viz= matching this visualization
  useEffect(() => {
    if (!visualizationId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('viz') === visualizationId) {
      setIsFullscreen(true);
      // Mirror the toggleFullscreen path: fullscreen always opens fit-to-viewport
      // so the viz fills the screen instead of sitting tiny in a sea of canvas.
      setViewMode('fit');
    }
  }, [visualizationId]);

  // Track container width for scale calculations
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isFullscreen]);

  // Close format dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (formatDropdownRef.current && !formatDropdownRef.current.contains(e.target as Node)) {
        setShowFormatDropdown(false);
      }
    };
    if (showFormatDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFormatDropdown]);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'scroll' ? 'fit' : 'scroll');
  }, []);

  // Handle escape key to exit fullscreen or close preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showExportPreview) {
          setShowExportPreview(false);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
    };
    
    if (isFullscreen || showExportPreview) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isFullscreen, showExportPreview]);

  // Remember the inline viewMode so we can restore it on exit. Fullscreen
  // forces 'fit' so the visualization always fills the viewport — anything
  // else leaves a small visual centered in a sea of empty canvas (the bug).
  const inlineViewModeRef = useRef<ViewMode>(initialViewMode);
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => {
      if (!prev) {
        inlineViewModeRef.current = viewMode;
        setViewMode('fit');
      } else {
        setViewMode(inlineViewModeRef.current);
      }
      return !prev;
    });
  }, [viewMode]);

  const effectiveTitle =
    isScreenshotMode && (exportData?.type === 'html' || exportData?.type === 'svg')
      ? undefined
      : title;
  const effectiveHeaderMode = isScreenshotMode ? 'overlay' : headerMode;
  const effectiveAllowFitToggle = isScreenshotMode ? false : allowFitToggle;
  const effectiveAllowFullscreen = isScreenshotMode ? false : allowFullscreen;
  const effectiveExportData = isScreenshotMode ? undefined : exportData;
  const effectiveRenderActions = isScreenshotMode ? undefined : renderActions;
  const shouldShowNormalHeader =
    Boolean(effectiveTitle) ||
    Boolean(effectiveRenderActions) ||
    effectiveAllowFitToggle ||
    Boolean(effectiveExportData) ||
    effectiveAllowFullscreen;

  // Update URL when fullscreen state changes (separate from render to avoid React warning)
  useEffect(() => {
    if (!visualizationId) return;

    const url = new URL(window.location.href);
    if (isFullscreen) {
      url.searchParams.set('viz', visualizationId);
    } else {
      url.searchParams.delete('viz');
    }
    window.history.replaceState({}, '', url.toString());
  }, [isFullscreen, visualizationId]);

  // Check for cached render first, then show modal or render
  const handleExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!exportData || isDownloading || isCheckingCache) return;

    try {
      setIsCheckingCache(true);
      
      // Check if we have a cached render
      const checkResponse = await fetch('/api/render/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: exportData.type,
          content: exportData.content,
        }),
      });

      const cacheData = await checkResponse.json();
      
      if (cacheData.cached && cacheData.url) {
        // Use cached image
        setExportedImage(cacheData.url);
        setShowExportPreview(true);
        return;
      }
      
      // No cache, need to render
      await renderImage();
    } catch (error) {
      console.error('Export check failed:', error);
      // Fall back to rendering
      await renderImage();
    } finally {
      setIsCheckingCache(false);
    }
  };

  // Actually render the image (called when no cache or regenerating)
  const renderImage = async () => {
    if (!exportData) return;

    try {
      setIsDownloading(true);

      // Capture the FULL rendered height so long visualizations (tall timelines, etc.) export completely.
      // Fall back to 800 if the container hasn't measured yet. Cap to prevent runaway sizes.
      const measuredHeight = containerRef.current?.scrollHeight ?? 0;
      const measuredWidth = containerRef.current?.clientWidth ?? 0;
      const captureWidth = Math.max(1200, Math.min(measuredWidth || 1200, 2400));
      const captureHeight = Math.max(800, Math.min(measuredHeight || 800, 16000));

      const response = await fetch('/api/render/screenshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visualizationId,
          type: exportData.type,
          content: exportData.content,
          width: captureWidth,
          height: captureHeight,
        }),
      });

      if (!response.ok) throw new Error('Render failed');

      const data = await response.json();
      if (data.success && data.url) {
        setExportedImage(data.url);
        setShowExportPreview(true);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Regenerate image (force new render)
  const handleRegenerate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowExportPreview(false);
    await renderImage();
    setShowExportPreview(true);
  };

  const handleDownloadImage = async () => {
    if (!exportedImage) return;

    const formatInfo = exportFormats.find(f => f.id === selectedFormat) || exportFormats[0];

    const sanitizedTitle = (title || 'visualization')
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .slice(0, 50);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-');
    const filename = `${sanitizedTitle}_${timestamp}.${formatInfo.extension}`;

    try {
      // Download via server proxy to avoid CORS and hide S3 URLs
      const response = await fetch('/api/render/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: exportedImage, filename }),
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const normalHeaderControls = (
    <>
      {effectiveRenderActions && (
        <span className={cn(
          effectiveHeaderMode === 'overlay' && 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity'
        )}>
          {effectiveRenderActions}
        </span>
      )}
      {effectiveAllowFitToggle && (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleViewMode}
          className={cn(
            'shrink-0 h-7 sm:h-8 px-1.5 sm:px-2',
            effectiveHeaderMode === 'overlay' && 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity'
          )}
          title={viewMode === 'scroll' ? 'Zoom out to see all' : 'Scroll to navigate'}
          aria-label={viewMode === 'scroll' ? 'Fit visualization to viewport' : 'Switch to scroll view'}
        >
          {viewMode === 'scroll' ? (
            <ArrowsInSimple weight="bold" className="size-3.5 sm:size-4" aria-hidden="true" />
          ) : (
            <ArrowsOutSimple weight="bold" className="size-3.5 sm:size-4" aria-hidden="true" />
          )}
          <span className="ml-1 text-xs hidden md:inline">
            {viewMode === 'scroll' ? 'Fit All' : 'Scroll'}
          </span>
        </Button>
      )}
      {effectiveExportData && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          disabled={isDownloading || isCheckingCache}
          className={cn(
            'shrink-0 h-7 sm:h-8 px-1.5 sm:px-2',
            effectiveHeaderMode === 'overlay' && 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity'
          )}
          title="Export as image"
          aria-label={
            isCheckingCache
              ? 'Checking export cache'
              : isDownloading
              ? 'Exporting visualization'
              : 'Export visualization as image'
          }
        >
          {(isDownloading || isCheckingCache) ? (
            <Spinner className="animate-spin size-3.5 sm:size-4" aria-hidden="true" />
          ) : (
            <ImageIcon weight="bold" className="size-3.5 sm:size-4" aria-hidden="true" />
          )}
          <span className="ml-1 text-xs hidden md:inline">
            {isCheckingCache ? 'Checking...' : isDownloading ? 'Exporting...' : 'Export'}
          </span>
        </Button>
      )}
      {effectiveAllowFullscreen && (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleFullscreen}
          className={cn(
            'shrink-0 h-7 sm:h-8 px-1.5 sm:px-2',
            effectiveHeaderMode === 'overlay' && 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity'
          )}
          title="Open fullscreen"
          aria-label="Open visualization fullscreen"
        >
          <ArrowsOut weight="bold" className="size-3.5 sm:size-4" aria-hidden="true" />
          <span className="ml-1 text-xs hidden md:inline">Fullscreen</span>
        </Button>
      )}
    </>
  );

  return (
    <>
      {effectiveHeaderMode === 'outside' && shouldShowNormalHeader && (
        <div className="mb-2 flex items-center justify-between gap-3 px-1">
          <div className="min-w-0 flex-1">
            {effectiveTitle && <h3 className="truncate text-sm font-medium text-foreground/90">{effectiveTitle}</h3>}
          </div>
          <div className="flex items-center gap-1 text-foreground/80">
            {normalHeaderControls}
          </div>
        </div>
      )}

      {/* Normal view */}
      <div
        ref={containerRef}
        className={cn(
          'w-full border rounded-lg overflow-hidden bg-card relative group',
          className
        )}
        style={
          // When fit-to-screen is active, CONSTRAIN the wrapper to a viewport-relative height
          // so the inner renderer can compute a proper scale factor (content > wrapper height).
          viewMode === 'fit'
            ? { height: '70vh', maxHeight: '70vh' }
            : flexHeight
              ? { minHeight: `${minHeight}px` }
              : { height: `min(${minHeight}px, 70vh)` }
        }
      >
        {effectiveHeaderMode === 'overlay' && shouldShowNormalHeader && (
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-b from-background/90 to-transparent">
            {effectiveTitle && <h3 className="font-semibold text-[10px] sm:text-xs md:text-sm truncate max-w-[60%] sm:max-w-[70%]">{effectiveTitle}</h3>}
            <div className="ml-auto flex items-center gap-0.5 sm:gap-1">
              {normalHeaderControls}
            </div>
          </div>
        )}
        
        {/* Content - needs explicit height for ReactFlow */}
        <div className={cn('w-full', effectiveHeaderMode === 'overlay' && shouldShowNormalHeader && 'pt-10')} style={{ height: 'calc(100% - 0px)' }}>
          <VisualizationProvider value={{ viewMode, isFullscreen, scale: 1, containerWidth }}>
            {children}
          </VisualizationProvider>
        </div>
      </div>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-background flex flex-col"
          >
            {/* Fullscreen header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
              {effectiveTitle && <h3 className="font-semibold">{effectiveTitle}</h3>}
              <div className="ml-auto flex items-center gap-2">
                {effectiveRenderActions}
                {effectiveAllowFitToggle && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleViewMode}
                    title={viewMode === 'scroll' ? 'Zoom out to see all' : 'Scroll to navigate'}
                  >
                    {viewMode === 'scroll' ? (
                      <ArrowsInSimple weight="bold" className="size-4" />
                    ) : (
                      <ArrowsOutSimple weight="bold" className="size-4" />
                    )}
                    <span className="ml-1">
                      {viewMode === 'scroll' ? 'Fit All' : 'Scroll'}
                    </span>
                  </Button>
                )}
                {effectiveExportData && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    disabled={isDownloading || isCheckingCache}
                  >
                    {(isDownloading || isCheckingCache) ? <Spinner className="animate-spin size-4" /> : <ImageIcon weight="bold" className="size-4" />}
                    <span className="ml-1">
                      {isCheckingCache ? 'Checking...' : isDownloading ? 'Exporting...' : 'Export'}
                    </span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleFullscreen}
                >
                  <X weight="bold" className="size-4" />
                  <span className="ml-1">Close</span>
                </Button>
              </div>
            </div>
            
            {/* Fullscreen content */}
            <div className="flex-1 overflow-auto">
              <VisualizationProvider value={{ viewMode, isFullscreen, scale: 1, containerWidth: window.innerWidth }}>
                {children}
              </VisualizationProvider>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Export Preview Modal */}
      <AnimatePresence>
      {showExportPreview && exportedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowExportPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-lg overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
                <h3 className="font-semibold">{title || 'Exported Image'}</h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerate}
                    disabled={isDownloading}
                    title="Regenerate image"
                  >
                    {isDownloading ? (
                      <Spinner className="size-4 animate-spin" />
                    ) : (
                      <ArrowClockwise weight="bold" className="size-4" />
                    )}
                    <span className="ml-1 hidden sm:inline">Regenerate</span>
                  </Button>
                  
                  {/* Format selector */}
                  <div className="relative" ref={formatDropdownRef}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFormatDropdown(!showFormatDropdown)}
                      className="min-w-[80px]"
                    >
                      {exportFormats.find(f => f.id === selectedFormat)?.label}
                      <CaretDown weight="bold" className="size-3 ml-1" />
                    </Button>
                    
                    <AnimatePresence>
                      {showFormatDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 top-full mt-1 z-10 bg-popover border rounded-md shadow-lg min-w-[100px] py-1"
                        >
                          {exportFormats.map(format => (
                            <button
                              key={format.id}
                              onClick={() => {
                                setSelectedFormat(format.id);
                                setShowFormatDropdown(false);
                              }}
                              className={cn(
                                "w-full px-3 py-1.5 text-left text-sm flex items-center justify-between hover:bg-muted transition-colors",
                                selectedFormat === format.id && "text-primary"
                              )}
                            >
                              {format.label}
                              {selectedFormat === format.id && (
                                <Check weight="bold" className="size-3" />
                              )}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleDownloadImage}
                  >
                    <Download weight="bold" className="size-4 mr-1" />
                    Download
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowExportPreview(false)}
                  >
                    <X weight="bold" className="size-4" />
                  </Button>
                </div>
              </div>
              
              {/* Image preview */}
              <div className="flex-1 overflow-auto p-4 bg-muted/30 flex items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img 
                  src={exportedImage} 
                  alt={title || 'Exported visualization'}
                  className="max-w-full max-h-full object-contain rounded shadow-lg"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
